/**
 * Brazil's rules for the automatic phone-number change (D643, doc 25 section 8, owner answer Q-359) - country-pack data.
 *
 *  - the PROOFS the pack names (`BRAZIL_ACCESS_PROOFS`): the national document is the CPF (check digits verified, written with or without the mask),
 *    a date is written day first, a phone number is a Brazilian national or E.164 number, and the words that name how a ride was paid (dinheiro,
 *    Pix, cartão). The shared kernel (`@vello/access-recovery`) knows none of this;
 *  - the first VERSION of the governed `central.accessRecovery` for Brazil (`BRAZIL_ACCESS_RECOVERY_V1`): the risk weights and thresholds, the attempt
 *    limits and cool-downs, the 72-hour hold after a change and the 72-hour window of the "não fui eu" link. An OWNER-EDITABLE starting point,
 *    published through the kernel's four-eyes seed path like the other Brazil versions; a change is a new version, never an edit of this file on a running market.
 */
import { extractNumericDates, phoneCandidates } from '@vello/access-recovery';
import { centralAccessRecoverySetting } from '@vello/config';
import { BRAZIL } from './brazil.js';
import { brazilPhone, isValidCpf } from './contact.js';
import { brazilKycCatalog } from './kyc-catalog.js';
const digits = (v) => v.replace(/\D/g, '');
/** The CPF in a free text: eleven digits with or without the mask, with valid check digits. */
function extractCpf(text) {
    for (const m of text.matchAll(/(?<![\d.\-/])(\d{3})\.?(\d{3})\.?(\d{3})-?(\d{2})(?![\d])/g)) {
        const d = `${m[1]}${m[2]}${m[3]}${m[4]}`;
        if (isValidCpf(d))
            return d;
    }
    return undefined;
}
export const BRAZIL_ACCESS_PROOFS = {
    countryCode: BRAZIL,
    version: 'br.accessProofs#v1',
    document: {
        type: 'CPF',
        labels: { pt: 'CPF', en: 'CPF (tax id)', es: 'CPF (documento fiscal)', it: 'CPF (codice fiscale brasiliano)' },
        extract: extractCpf,
        normalize: (raw) => (isValidCpf(raw.trim()) ? digits(raw) : undefined),
    },
    extractDates: (text) => extractNumericDates(text, 'dmy'),
    extractPhones(text) {
        const out = [];
        for (const candidate of phoneCandidates(text)) {
            // A CPF is eleven digits too: a candidate that is a valid CPF is a document, not a phone.
            if (!candidate.startsWith('+') && isValidCpf(candidate))
                continue;
            const phone = brazilPhone(candidate);
            if (phone.ok)
                out.push(phone.value.e164);
        }
        return out;
    },
    // The person documents Brazil's KYC catalogue requires WITH the holder's photo (the driver licence): one source, the catalogue's own `photo` rule.
    faceDocumentTypes: brazilKycCatalog().types.filter((t) => t.subject === 'PERSON' && t.rules.photo).map((t) => t.code),
    paymentMethodWords: {
        cash: ['dinheiro', 'especie', 'cash', 'efectivo', 'contanti'],
        instant_payment: ['pix', 'pagamento instantaneo', 'transferencia'],
        card: ['cartao', 'credito', 'debito', 'card', 'tarjeta', 'carta'],
    },
};
/** When the first version takes effect (the day doc 25 was approved, UTC). */
export const BRAZIL_ACCESS_RECOVERY_EFFECTIVE_FROM = '2026-10-19T00:00:00Z';
/**
 * Brazil's first policy. A score under 30 is LOW (automatic), 30 to 59 MEDIUM (one more proof), 60 and over HIGH (the safety team). An open safety case
 * is 60 on its own; four wrong proofs reach 60 as well; a young account (14 days), an e-mail changed in the last 30 days or a profile re-enrolled in the last
 * 30 days is medium on its own; a phone changed in the last 30 days is medium on its own; pending payouts and a channel never seen add to the score. Five wrong
 * proofs end a claim; three claims a day per account or number; an hour of cool-down; a claim lives an hour; a code ten minutes with five tries; the hold and
 * the window of the "não fui eu" link are 72 hours (owner Q-359).
 */
export const BRAZIL_ACCESS_RECOVERY_V1 = {
    thresholds: { mediumFrom: 30, highFrom: 60 },
    weights: { accountYoung: 30, emailRecentlyChanged: 35, phoneRecentlyChanged: 30, profileRecentlyChanged: 30, failedProof: 15, newChannel: 10, pendingPayouts: 20, openSafetyCase: 60 },
    windows: { youngAccountDays: 14, recentChangeDays: 30 },
    limits: { maxFailedProofs: 5, maxClaimsPerAccountPerDay: 3, maxClaimsPerPhonePerDay: 3, cooldownMinutes: 60, claimTtlMinutes: 60, codeTtlMinutes: 10, maxCodeAttempts: 5 },
    holdHours: 72,
    revertWindowHours: 72,
};
export function brazilAccessRecoverySettings() {
    return [centralAccessRecoverySetting({ scopeRef: BRAZIL, value: BRAZIL_ACCESS_RECOVERY_V1, version: 1, effectiveFrom: BRAZIL_ACCESS_RECOVERY_EFFECTIVE_FROM })];
}
/** Publish Brazil's access-recovery version through a (governed) policy `publish`. Returns the count accepted. */
export async function publishBrazilAccessRecoveryPolicies(publish, newId, now) {
    let count = 0;
    for (const setting of brazilAccessRecoverySettings()) {
        if (await publish(setting, newId(), now))
            count += 1;
    }
    return count;
}
//# sourceMappingURL=access-recovery.js.map